import QRCode from "qrcode";

export type PalletQrLabel = {
    /// Conteúdo do QR = código do palete (PLT-000123). O leitor casa por código.
    code: string;
    scanCode?: string | null;
    title: string;
    subtitle?: string;
    lines?: string[];
};

const escapeHtml = (value: string) =>
    value.replace(
        /[&<>"']/g,
        (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[character]!,
    );

const pageStyles = `
    @page { size: A4 portrait; margin: 10mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { color: #10261b; font-family: Arial, Helvetica, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .pallet-page {
        height: 277mm;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        break-after: page;
        page-break-after: always;
        border: 2mm solid #2b6b50;
        border-radius: 6mm;
        padding: 6mm;
    }
    .pallet-page:last-child { break-after: auto; page-break-after: auto; }
    .pallet-title { font-size: 15mm; font-weight: 900; letter-spacing: 1mm; color: #2b6b50; line-height: 1; }
    .pallet-subtitle { font-size: 7.5mm; font-weight: 700; margin-top: 2.5mm; }
    /* O QR é a informação mais importante: ocupa quase toda a largura útil. */
    .pallet-qr { width: 182mm; height: 182mm; margin: 5mm 0; }
    .pallet-qr svg { width: 100%; height: 100%; shape-rendering: crispEdges; }
    .pallet-lines { font-size: 6mm; color: #33443c; }
    .pallet-lines div { margin-top: 1.2mm; }
`;

async function renderPallet(label: PalletQrLabel) {
    const payload = label.scanCode?.trim() || label.code;
    const qr = await QRCode.toString(payload, { type: "svg", errorCorrectionLevel: "H", margin: 2 });
    const lines = (label.lines ?? []).map((line) => `<div>${escapeHtml(line)}</div>`).join("");
    return `<section class="pallet-page">
        <div class="pallet-title">${escapeHtml(label.title)}</div>
        ${label.subtitle ? `<div class="pallet-subtitle">${escapeHtml(label.subtitle)}</div>` : ""}
        <div class="pallet-qr">${qr}</div>
        <div class="pallet-lines">${lines}</div>
    </section>`;
}

export async function printPalletQrLabels(pallets: PalletQrLabel[]) {
    if (!pallets.length) throw new Error("Nenhum palete para imprimir.");
    const printWindow = window.open("", "_blank");
    if (!printWindow) throw new Error("O navegador bloqueou a janela de impressão.");
    printWindow.document.write('<p style="font-family:Arial;padding:24px">Preparando etiqueta…</p>');
    try {
        const pages = await Promise.all(pallets.map(renderPallet));
        printWindow.document.open();
        printWindow.document.write(`<!doctype html>
            <html lang="pt-BR">
                <head>
                    <meta charset="utf-8" />
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <title>QR do palete</title>
                    <style>${pageStyles}</style>
                </head>
                <body>${pages.join("")}</body>
            </html>`);
        printWindow.document.close();
        await new Promise<void>((resolve) => printWindow.requestAnimationFrame(() => resolve()));
        printWindow.addEventListener("afterprint", () => printWindow.close(), { once: true });
        printWindow.focus();
        printWindow.print();
    } catch (error) {
        printWindow.close();
        throw error;
    }
}
